import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import {
  addRole,
  createUserAccount,
  doAuth,
  enrollUserTOTP,
  getAccessLogs,
  getUserAccountById,
  getUserAccounts,
  removeRole,
  setPwdResetRequired,
  setTOTPRequired,
  setUserAccountActive,
  setUserAccountPassword,
  setUserDisplayName,
} from "./api"
import { allowUpdate } from "../../app/store"
import { jwtDecode } from "jwt-decode"
import {
  faBuilding,
  faPersonMilitaryPointing,
  faSatelliteDish,
  faUpload,
  faUser,
} from "@fortawesome/free-solid-svg-icons"

// Key used to store token in localstorage
export const TOKEN_KEY = "token"

const initialState = {
  roles: {
    viewer: {
      name: "viewer",
      colorCode: "#E7E6E6",
      icon: faUser,
      description: "General user able to browse information and entities",
    },
    deployer: {
      name: "deployer",
      colorCode: "#66FFCC",
      icon: faUpload,
      description: "Able to generate observation schedules and task hardware",
    },
    "org-admin": {
      name: "org-admin",
      colorCode: "#FF8C00",
      icon: faBuilding,
      description:
        "Manages users, entities and preferences for their organisation",
    },
    admin: {
      name: "admin",
      colorCode: "#FF3F6F",
      icon: faPersonMilitaryPointing,
      description:
        "Administrative super-user with full control over all users and organisations",
    },
    telescope: {
      name: "telescope",
      colorCode: "#FFA900",
      icon: faSatelliteDish,
      description: "Represents a sensor (hardware device) out in the field",
    },
  },
  byId: {}, // A map of accounts by id
  accessLogsById: {}, // A map of access logs by id
  matchesByCtx: {
    // root context powering the user account list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // accounts by id
      page: {}, // Pagination information
    },
  },
  accessLogMatchesByCtx: {
    // root context powering the user access logs list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // access logs by id
      page: {}, // Pagination information
    },
  },
  creating: {}, // space for new user accounts that are in the process of being created
  updating: {}, // space for user accounts that are in the process of being updated
  deletingRole: {}, // space for deletion of role process
  // Current user. Note: Token is stored in local storage.
  auth: {
    /* id, roles, _loadStatus */
  },
  // If populated with a string, user must first
  user_agreement_text: null,
}

// Best-effort: Initial state auth from local storage token
try {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    initialState.auth = jwtDecode(token)
    initialState.auth.token = token
  }
} catch (error) {
  console.warn("Failed to init auth token from localStorage", error)
}

/**
 * Authenticate with username and password.
 * @type {AsyncThunk<*, void, string>}
 */
export const login = createAsyncThunk(
  "iam/login",
  async ({ id, password, totp }, { rejectWithValue }) => {
    try {
      return await doAuth({ id, password, totp })
    } catch (e) {
      throw rejectWithValue(e)
    }
  }
)

/** Enroll user for TOTP (2FA) */
export const totpEnroll = createAsyncThunk(
  "iam/totp/enroll",
  async ({ id }, { rejectWithValue }) => {
    try {
      return await enrollUserTOTP(id)
    } catch (e) {
      // FIXME: How to return actual arror details?
      throw rejectWithValue("something went wrong")
    }
  }
)

/** Change whether a user requires TOTP (2FA) */
export const totpRequired = createAsyncThunk(
  "iam/totp/required",
  async ({ id, required }, { rejectWithValue }) => {
    try {
      return await setTOTPRequired(id, required)
    } catch (e) {
      // FIXME: How to return actual arror details?
      throw rejectWithValue("something went wrong")
    }
  }
)

/**
 * Thunk to remove current user's auth (log out)
 * @type {AsyncThunk<*, void, string>}
 */
export const logout = createAsyncThunk("iam/logout", async () => {
  localStorage.removeItem(TOKEN_KEY)
  return Promise.resolve()
})

/**
 * Thunk to perform async logic to fetch user accounts matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchUserAccounts = createAsyncThunk(
  "iam/accounts/fetch",
  /**
   * @param filter the filters to apply
   * @param page Pagination information
   * @param context The context from where the query is being made.
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getUserAccounts({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Thunk to perform async logic to fetch a user account by its ID
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchUserAccountById = createAsyncThunk(
  "iam/account/fetch",
  async (accountId) => {
    return await getUserAccountById(accountId)
  }
)

/**
 * Thunk to perform async logic to set a user active or inactive
 * @type {AsyncThunk<*, void, {}>}
 */
export const updateUserAccountActive = createAsyncThunk(
  "iam/account/update/active",
  async ({ id, active, _etag }, { rejectWithValue }) => {
    try {
      return await setUserAccountActive({ id, active, _etag })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to set a user account display name
 * @type {AsyncThunk<*, void, {}>}
 */
export const updateUserAccountDisplayName = createAsyncThunk(
  "iam/account/update/name",
  async ({ id, name, _etag }, { rejectWithValue }) => {
    try {
      return await setUserDisplayName({ id, name, _etag })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to add a role to an account
 * @type {AsyncThunk<*, void, {}>}
 */
export const addRoleToUserAccount = createAsyncThunk(
  "iam/account/role/add",
  async ({ id, role, ctx, _etag }, { rejectWithValue }) => {
    try {
      return await addRole({ id, role, ctx, _etag })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to remove a role from an account
 * @type {AsyncThunk<*, void, {}>}
 */
export const removeRoleFromUserAccount = createAsyncThunk(
  "iam/account/role/remove",
  async ({ id, role, ctx, _etag }, { rejectWithValue }) => {
    try {
      return await removeRole({ id, role, ctx, _etag })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to create a user account
 * @type {AsyncThunk<*, void, {}>}
 */
export const addUserAccount = createAsyncThunk(
  "iam/account/create",
  async (req = {}, { rejectWithValue }) => {
    try {
      // we only support one role for now on creation
      let roles = []
      if (req.role) {
        roles.push({
          role: req.role,
          ctx: req.ctx,
        })
      }
      return await createUserAccount({
        email: req.email,
        display_name: req.display_name,
        password: req.password,
        confirmation: req.confirmation,
        is_active: req.active,
        roles,
        password_reset_required: req.password_reset_required,
        token_lifetime: req.token_lifetime,
      })
    } catch (error) {
      throw rejectWithValue(error)
    }
  }
)

/**
 * Thunk to perform async logic to set a user account password
 * @type {AsyncThunk<*, void, {}>}
 */
export const updateUserAccountPassword = createAsyncThunk(
  "iam/account/update/pwd",
  async (
    { id, current_password, password, confirmation, _etag },
    { rejectWithValue }
  ) => {
    try {
      return await setUserAccountPassword({
        id,
        current_password,
        password,
        confirmation,
        _etag,
      })
    } catch (error) {
      throw rejectWithValue(error)
    }
  }
)

/**
 * Thunk to perform async logic to set mark that a user must reset their password
 * on the next login
 *
 * @type {AsyncThunk<*, void, {}>}
 */
export const setPasswordResetRequired = createAsyncThunk(
  "iam/account/pwd/reset",
  async ({ ids = [] }, { rejectWithValue }) => {
    try {
      return await setPwdResetRequired({ ids })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to fetch the access logs matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchAccessLogs = createAsyncThunk(
  "iam/access-logs/fetch",
  /**
   *
   * @param filter the filters to apply
   * @param page Pagination information
   * @param context The context from where the query is being made.
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getAccessLogs({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * IAM reducer
 */
export const iamSlice = createSlice({
  name: "iam",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    clearMatches: (state, action) => {
      state.matchesByCtx[action?.payload || "root"].ids = []
    },
    /** When a user wants to reset their process of logging in / resetting password / whatever
     * to start over.*/
    resetAuth: (state, action) => {
      state.auth = { id: action?.payload }
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserAccounts.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchUserAccounts.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"
          if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
            // Don't reset if we are paging
            if (action?.payload?.page?.start_index === 0) {
              state.matchesByCtx[context].ids = []
            }
            action?.payload?.data?.map((account) => {
              // Add the accounts to state by id (this can include ids not retrieved in this call)
              state.byId[account.id] = {
                ...account,
                _at: action.payload.at,
              }
              // add the ids of the accounts that match from this query
              if (state.matchesByCtx[context].ids.indexOf(account.id) === -1) {
                state.matchesByCtx[context].ids.push(account.id)
              }
            })
            state.matchesByCtx[context].page = action.payload.page
            state.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchUserAccounts.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(fetchUserAccountById.pending, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "loading"
      })
      .addCase(fetchUserAccountById.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          if (
            allowUpdate(
              state.byId[action.payload.id]
                ? state.byId[action.payload.id]._at
                : undefined,
              action.payload._at
            )
          ) {
            state.byId[action.payload.id] = action.payload
            state.byId[action.payload.id]._loadStatus = "idle"
          }
        }
      })
      .addCase(fetchUserAccountById.rejected, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "rejected"
        state.byId[action.meta.arg]._errorMessage += action.payload
      })
      .addCase(updateUserAccountActive.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "updating"
      })
      .addCase(updateUserAccountActive.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
          state.byId[action.payload.id]._saveStatus = "idle"
        }
      })
      .addCase(updateUserAccountActive.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.id]._errorMessage = action.payload
      })
      .addCase(updateUserAccountDisplayName.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "updating"
      })
      .addCase(updateUserAccountDisplayName.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
          state.byId[action.payload.id]._saveStatus = "idle"
        }
      })
      .addCase(updateUserAccountDisplayName.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.id]._errorMessage = action.payload
      })
      .addCase(addUserAccount.pending, (state) => {
        if (!state["creating"]) {
          state["creating"] = {}
        }
        state["creating"]._saveStatus = "creating"
      })
      .addCase(addUserAccount.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
          state.matchesByCtx.root.ids.push(action.payload.id)
        }
        state["creating"]._saveStatus = "idle"
      })
      .addCase(addUserAccount.rejected, (state, action) => {
        state["creating"]._saveStatus = "rejected"
        state["creating"]._errorMessage = action.payload?.error
      })
      .addCase(addRoleToUserAccount.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "updating"
      })
      .addCase(addRoleToUserAccount.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
          state.byId[action.payload.id]._saveStatus = "idle"
        }
      })
      .addCase(addRoleToUserAccount.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.id]._errorMessage = action.payload
      })
      .addCase(removeRoleFromUserAccount.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "updating"
      })
      .addCase(removeRoleFromUserAccount.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
          state.byId[action.payload.id]._saveStatus = "idle"
        }
      })
      .addCase(removeRoleFromUserAccount.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.id]._errorMessage = action.payload
      })
      .addCase(totpRequired.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "updating"
      })
      .addCase(totpRequired.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
          state.byId[action.payload.id]._saveStatus = "idle"
        }
      })
      .addCase(totpRequired.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.id]._errorMessage = action.payload
      })
      .addCase(totpEnroll.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "updating"
      })
      .addCase(totpEnroll.fulfilled, (state, action) => {
        if (action?.meta.arg?.id) {
          state.byId[action.meta.arg?.id]._saveStatus = "idle"
        }
      })
      .addCase(totpEnroll.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.id]._errorMessage = action.payload
      })
      .addCase(updateUserAccountPassword.pending, (state) => {
        if (!state["updating"]) {
          state["updating"] = {}
        }
        state["updating"]._saveStatus = "updating"
      })
      .addCase(updateUserAccountPassword.fulfilled, (state) => {
        state["updating"]._saveStatus = "idle"
      })
      .addCase(updateUserAccountPassword.rejected, (state, action) => {
        state["updating"]._saveStatus = "rejected"
        state["updating"]._errorMessage = action.payload?.error
      })
      .addCase(login.pending, (state) => {
        state.auth._loadStatus = "loading"
      })
      .addCase(login.fulfilled, (state, action) => {
        if (action.payload.token) {
          // Store in state (decoded)
          state.auth = jwtDecode(action.payload.token)
          state.auth.token = action.payload.token
          // Service may have returned an agreement that user must first accept
          state.user_agreement_text = action.payload.user_agreement_text
          // Store in localstorage for future visits (raw)
          // TODO: Don't do I/O / Side-effects here, move to action creator
          localStorage.setItem(TOKEN_KEY, action.payload.token)
        }
        state.auth._loadStatus = "idle"
      })
      .addCase(login.rejected, (state, action) => {
        state.auth._loadStatus = "rejected"
        state.auth._errorMessage = action.payload?.error
        // Other props, like details such as password_reset_required
        state.auth = { ...state.auth, ...action.payload }

        // Clean any prior token from localStorage
        // TODO: Don't do I/O / Side-effects here, move to action creator
        localStorage.removeItem(TOKEN_KEY)
        state.auth.sub = null
        state.auth.roles = null
      })
      .addCase(logout.fulfilled, (state) => {
        state.auth = null
      })
      .addCase(setPasswordResetRequired.pending, (state) => {
        if (!state["updating"]) {
          state["updating"] = {}
        }
        state["updating"]._saveStatus = "updating"
      })
      .addCase(setPasswordResetRequired.fulfilled, (state, action) => {
        state["updating"]._saveStatus = "idle"
        action.meta.arg?.ids?.forEach((id) => {
          if (state.byId[id]) {
            state.byId[id].password_reset_required = true
          }
        })
      })
      .addCase(setPasswordResetRequired.rejected, (state, action) => {
        state["updating"]._saveStatus = "rejected"
        state["updating"]._errorMessage = action.payload
      })
      .addCase(fetchAccessLogs.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.accessLogMatchesByCtx[context]) {
          state.accessLogMatchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.accessLogMatchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchAccessLogs.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.accessLogMatchesByCtx[context]) {
          state.accessLogMatchesByCtx[context]._loadStatus = "idle"
          if (
            allowUpdate(
              state.accessLogMatchesByCtx[context].at,
              action.payload.at
            )
          ) {
            // Don't reset if we are paging
            if (action?.payload?.page?.start_index === 0) {
              state.accessLogMatchesByCtx[context].ids = []
            }
            action?.payload?.data?.map((accessLog) => {
              // Add to state by id (this can include ids not retrieved in this call)
              state.accessLogsById[accessLog.id] = {
                ...accessLog,
                _at: action.payload.at,
              }
              if (
                state.accessLogMatchesByCtx[context].ids.indexOf(
                  accessLog.id
                ) === -1
              ) {
                state.accessLogMatchesByCtx[context].ids.push(accessLog.id)
              }
            })
            state.accessLogMatchesByCtx[context].page = action.payload.page
            state.accessLogMatchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchAccessLogs.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.accessLogMatchesByCtx[context]._loadStatus = "rejected"
        state.accessLogMatchesByCtx[context]._errorMessage =
          action?.error?.message
      })
  },
})

// Selector for all user accounts
export const selectUserAccountsById = (state) => state.iam.byId
export const selectAccessLogsById = (state) => state.iam.accessLogsById
export const selectCreatingUserAccount = (state) => state.iam.creating
export const selectUpdatingUserAccount = (state) => state.iam.updating
// Selects all available roles, as {rolename -> Role} object
export const selectUserAccountsRoles = (state) => state.iam.roles
// Selector for the roles a user account can have
export const selectRoles = (state) => state.iam.roles
// Selects current user's auth state
export const selectAuth = (state) => state.iam.auth

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectUserAccountsByContext(state, `rso/${id}`))
 */
export const selectUserAccountsByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.iam.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`accounts, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectUserAccountsByIds(state, ["1", "2"]))
 */
export const selectUserAccountsByIds = createSelector(
  [
    // first input - extract value from `state`
    selectUserAccountsById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

/**
 * Takes in a context parameter and returns matches from accessLogMatchesByCtx[context]
 */
export const selectAccessLogsByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.iam.accessLogMatchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`accounts, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectUserAccountsByIds(state, ["1", "2"]))
 */
export const selectAccessLogsByIds = createSelector(
  [
    // first input - extract value from `state`
    selectAccessLogsById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

export const { resetAuth } = iamSlice.actions

export default iamSlice.reducer
